import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import * as angular from 'angular';
import { downgradeComponent } from '@angular/upgrade/static';
import { Store } from '@ngrx/store';
import { filter, first, map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { GenericListType, LoadMoreQuery } from '../../common/components/lists/generic-list.component';
import { AppState } from '../../store';
import {
  closeClustersModal,
  getClustersList,
  loadMoreClustersList,
  openClustersModal,
  searchClustersList,
} from '../store/clusters.actions';
import {
  selectAllClustersLoadedFlag,
  selectClusters,
  selectClustersErrorFlag,
  selectClustersIsModalOpenFlag,
  selectClustersLoadingFlag,
  selectLastlyCreatedCluster,
} from '../store/clusters.selectors';
import { Cluster, CLUSTERS_VISIBLE_STATUSES, ClusterTypes } from '../clusters.models';
import { AssistedSearchClustersConfiguratorService } from '../../common/services/assisted-search/assisted-search-clusters-configurator.service';
import { SearchConfig } from '../../common/services/assisted-search/search-config.model';
import { selectJobsIsModalOpenFlag } from '../../jobs/store/jobs.selectors';
import { closeJobsModal, openJobsModal } from '../../jobs/store/jobs.actions';
import { Job } from '../../jobs/jobs.models';
import { loadSelectPickerItems } from '../../common/store/select-picker.actions';
import {
  queryParamsMap,
  SelectPickerTypes,
} from '../../common/components/forms/select-picker/select-picker-types.enum';
import { QueryParamsClustersList } from '../../common/helper/query-params-generic-list.helper';

const defaultCluster: Partial<Cluster> = {
  name: '',
  type: ClusterTypes.sandbox,
  nodes: 1,
  terminate_on_idle: true,
  time_to_idle: 600,
  reuse_cluster_strategy: 'any',
};

@Component({
  selector: 'clusters-list',
  template: `
    <generic-list
      [type]="type"
      [items]="clusters$ | async"
      [isLoading]="clustersLoading$ | async"
      [hasNewButton]="true"
      (createClick)="openClusterCreateModal()"
      [newBtnText]="'cluster.generic-object.buttons.new' | translate"
      [emptyTitle]="'cluster.generic-object.empty.title' | translate"
      [emptyDescription]="'cluster.generic-object.empty.description' | translate"
      (loadMore)="loadMoreClusters($event)"
      [allItemsLoaded]="areAllClustersLoaded$ | async"
      [limit]="queryParams.limit"
      [searchPlaceholder]="'cluster.generic-object.placeholders.search' | translate"
      [searchConfig]="searchConfig"
      [searchQueryFromUrl]="searchQueryFromUrl"
      [hasSearch]="true"
      [isError]="clustersError$ | async"
      (searchSubmit)="onSearchSubmit($event)"
    >
      <ng-template templateName="listItem" let-item>
        <cluster-list-item
          [item]="item"
          (edit)="editCluster($event)"
          (createJob)="openJobCreateModal($event)"
        ></cluster-list-item>
      </ng-template>
    </generic-list>
    <xp-modal
      [isOpen]="isClusterModalOpen$ | async"
      [closeButtonText]="'cluster.generic-object.buttons.close' | translate"
      [saveButtonText]="'cluster.generic-object.buttons.save' | translate"
      [titleText]="'cluster.generic-object.title' | translate"
      (close)="onClusterModalClose()"
      [isSmall]="true"
    >
      <ng-template>
        <cluster-form [item]="formItem"></cluster-form>
      </ng-template>
    </xp-modal>
    <xp-modal
      [isOpen]="isJobModalOpen$ | async"
      [closeButtonText]="'job.generic-object.buttons.close' | translate"
      [saveButtonText]="'job.generic-object.buttons.save' | translate"
      [titleText]="'job.generic-object.title' | translate"
      (close)="onJobModalClose()"
    >
      <ng-template>
        <job-form [item]="jobFormItem" [disableModalClose]="true"></job-form>
      </ng-template>
    </xp-modal>
  `,
})
export class ClustersListComponent implements AfterViewInit, OnInit, OnDestroy {
  type = GenericListType.clusters;
  clustersLoading$ = this.store.select(selectClustersLoadingFlag);
  clustersError$ = this.store.select(selectClustersErrorFlag);
  isClusterModalOpen$ = this.store.select(selectClustersIsModalOpenFlag);
  isJobModalOpen$ = this.store.select(selectJobsIsModalOpenFlag);
  areAllClustersLoaded$ = this.store.select(selectAllClustersLoadedFlag);
  clusters$ = this.store
    .select(selectClusters)
    .pipe(map((clusters) => clusters.filter((cluster) => CLUSTERS_VISIBLE_STATUSES.includes(cluster.status))));
  queryParams = QueryParamsClustersList;
  formItem: Partial<Cluster> = defaultCluster;
  searchQueryFromUrl = '';
  searchConfig: SearchConfig = {};
  jobFormItem: Partial<Job> = {};
  clusterSubscription: Subscription;

  constructor(
    @Inject('$rootScope') private $rootScope: any,
    private store: Store<AppState>,
    private assistedSearchClustersConfigurator: AssistedSearchClustersConfiguratorService,
    @Inject('$stateParams') private $stateParams: any,
  ) {
    this.clusterSubscription = this.store.select(selectLastlyCreatedCluster).subscribe((cluster) => {
      if (cluster) {
        this.store.dispatch(
          loadSelectPickerItems({
            id: 'cluster-picker-job-form',
            itemType: SelectPickerTypes.cluster,
            params: queryParamsMap[SelectPickerTypes.cluster],
          }),
        );
        this.jobFormItem = {
          ...this.jobFormItem,
          cluster_id: cluster.id,
        };
      }
    });
  }

  ngOnInit() {
    const query = this.$rootScope.getQueryParams('query');

    this.searchQueryFromUrl = query || '';

    this.searchConfig = this.assistedSearchClustersConfigurator.getConfig();

    if (!query) {
      this.store.dispatch(getClustersList({ params: this.queryParams }));
    }
  }

  ngAfterViewInit() {
    if (this.$rootScope.stateIs('app.account.clusters.new')) {
      this.openClusterCreateModal();
    }

    if (this.$rootScope.stateIs('app.account.clusters.item.edit')) {
      this.clusters$
        .pipe(
          filter((clusters) => clusters.length > 0),
          first(),
        )
        .subscribe((clusters) => {
          const foundCluster = clusters.find((item) => item.id === Number(this.$stateParams.id));
          if (!foundCluster) {
            this.$rootScope.stateIs('app.account.clusters');
          } else {
            this.editCluster(foundCluster);
          }
        });
    }
  }

  editCluster(item: Cluster) {
    this.formItem = { ...item };
    this.$rootScope.stateGo('app.account.clusters.item.edit', { id: item.id });
    this.store.dispatch(openClustersModal());
  }

  openClusterCreateModal(): void {
    this.formItem = { ...defaultCluster };
    this.$rootScope.stateGo('app.account.clusters.new');
    this.store.dispatch(openClustersModal());
  }

  openJobCreateModal(cluster: Cluster): void {
    this.jobFormItem = {
      cluster_id: cluster.id,
    };
    this.store.dispatch(openJobsModal());
  }

  onClusterModalClose(): void {
    this.$rootScope.stateGo('app.account.clusters');
    this.store.dispatch(closeClustersModal());
  }

  onJobModalClose(): void {
    this.$rootScope.stateGo('app.account.clusters');
    this.store.dispatch(closeJobsModal());
  }

  loadMoreClusters(query: LoadMoreQuery) {
    const params: any = {
      ...this.queryParams,
      ...query,
    };
    this.store.dispatch(loadMoreClustersList({ params }));
  }

  onSearchSubmit(query: string) {
    const params = { ...this.queryParams, q: query };
    this.queryParams = params;

    if (!query) {
      this.store.dispatch(getClustersList({ params: this.queryParams }));
    } else {
      this.store.dispatch(searchClustersList({ params }));
    }
  }

  ngOnDestroy() {
    if (this.clusterSubscription) {
      this.clusterSubscription.unsubscribe();
    }
  }
}

((window as any).xplenty as angular.IModule).directive(
  'clustersList',
  downgradeComponent({
    component: ClustersListComponent,
  }),
);
